<script setup lang="ts">
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogDescription,
} from '@headlessui/vue'
import IconChevronDown from '~/components/icons/IconChevronDown.vue'

const { event, isOfferPage = false } = defineProps<{
  event: EventDescription
  isOfferPage?: boolean
}>()
// The landscape video is decided on the client, never in SSR: behind
// CloudFront the origin never sees the User-Agent, so a device check here
// renders the desktop branch for phones too. The main image always renders (it
// is the LCP); the video mounts over it on md+ viewports that are also tall
// enough to rule out phones held sideways (~390–430px high), so phones never
// fetch it while tablets and desktops keep it.
const LANDSCAPE_VIDEO_MIN_HEIGHT = 600
const landscapeVideo = computed(() => eventGetters.getLandscapeVideo(event))
const portraitVideo = computed(() => eventGetters.getPortraitVideo(event))
const showsLandscapeVideo = useHydrationSafeMediaQuery(
  `${minWidthQuery('md')} and (min-height: ${LANDSCAPE_VIDEO_MIN_HEIGHT}px)`
)
const isPreviewModalOpen = ref(false)
const togglePreviewModalOpen = () => {
  isPreviewModalOpen.value = !isPreviewModalOpen.value
}
const featuredImages = computed(() => {
  return [
    ...eventGetters.getFeaturedImages(event).map((image: any) => ({
      ...image,
      src: image.cropped || image.original,
    })),
    ...eventGetters.getNonFeaturedImages(event).map((image: any) => ({
      ...image,
      src: image.cropped || image.original,
    })),
  ]
})

// Video-only events have no photo: use the landscape video's poster as the
// server-rendered main image so phones and the LCP still get a real image.
const principalImage = computed(() => {
  const image = eventGetters.getPrincipalImage(event)
  if (image) {
    return { ...image, src: image.cropped || image.original }
  }

  const poster = landscapeVideo.value?.thumb
  if (landscapeVideo.value && poster) {
    return {
      ...landscapeVideo.value,
      cropped: poster,
      src: poster,
      alt: landscapeVideo.value.alt || eventGetters.getTitle(event),
    }
  }

  return {}
})
</script>
<template>
  <div class="relative">
    <!--
      "Alle Fotos" sits on the gallery's top-right corner on phones and on the
      main image from md up (over the video when it mounts, otherwise over the
      image). Two CSS-toggled instances (one button each), not a useDevice()
      branch: SSR never sees the User-Agent behind CloudFront.
    -->
    <div class="contents md:hidden">
      <NuxtLazyHydrate when-idle>
        <EventGalleryActionButtons
          @click:open-preview-modal="togglePreviewModalOpen"
        />
      </NuxtLazyHydrate>
    </div>

    <EventGalleryGrid
      :principal-image="principalImage"
      :featured-images="featuredImages"
      :has-vertical-video="Boolean(portraitVideo)"
      :is-offer-page="isOfferPage"
      preload-principal-image
      @click:image="togglePreviewModalOpen"
    >
      <template #main-image-overlay>
        <LazyEventGalleryVideoPlayer
          v-if="showsLandscapeVideo && landscapeVideo"
          class="absolute inset-0 cursor-pointer"
          :allow-fullscreen="true"
          :source="landscapeVideo.original"
          :source-stream="landscapeVideo.streaming_url"
          :poster="landscapeVideo.thumb"
          orientation="horizontal"
          @click.stop
        />
      </template>
      <template #vertical-slot>
        <LazyEventGalleryVideoPlayer
          v-if="portraitVideo"
          class="w-full cursor-pointer"
          :allow-fullscreen="true"
          :source="portraitVideo.original"
          :source-stream="portraitVideo.streaming_url"
          :poster="portraitVideo.thumb"
          orientation="vertical"
        />
      </template>
      <template #action-buttons>
        <div class="hidden md:contents">
          <NuxtLazyHydrate when-idle>
            <EventGalleryActionButtons
              @click:open-preview-modal="togglePreviewModalOpen"
            />
          </NuxtLazyHydrate>
        </div>
      </template>
    </EventGalleryGrid>

    <ClientOnly>
      <TransitionRoot as="template" :show="isPreviewModalOpen">
        <Dialog class="relative z-100" @close="togglePreviewModalOpen">
          <div class="fixed inset-0 flex w-screen items-center justify-center">
            <TransitionChild
              enter="duration-300 ease-out"
              enter-from="opacity-0 translate-y-full"
              enter-to="opacity-100 translate-y-0"
              leave="duration-200 ease-in"
              leave-from="opacity-100 translate-y-0"
              leave-to="opacity-0 translate-y-full"
              as="template"
            >
              <DialogPanel class="flex flex-col w-full h-full bg-white">
                <DialogTitle class="p-4 text-base">
                  <div>
                    <Button
                      color="tertiary"
                      class="px-2 border border-gray-300 rounded-full!"
                      @click.prevent="togglePreviewModalOpen"
                    >
                      <IconChevronDown class="rotate-90" />
                    </Button>
                  </div>
                </DialogTitle>
                <DialogDescription class="p-4 overflow-x-auto">
                  <EventGalleryPreview :event="event" />
                </DialogDescription>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </TransitionRoot>
    </ClientOnly>
  </div>
</template>
